import {
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import {
    atlas,
    REPORTS_CREDIT_REQUEST_HISTORY
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    AddCreditRequestResponse,
    CreditRequestStatus,
    SearchDebtor,
    SearchDebtorsRequest,
    SearchProvider
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    AddCreditRequestRequest,
    AddDebtorForClientRequest,
    BatchAddCreditRequestsRequest,
    BatchAddCreditRequestsService,
    BatchAddDebtorsForClientRequest,
    BatchAddDebtorsForClientService,
    SearchDebtorsService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    AmericanStateAbbreviations,
    AmericanStatesAndTerritoriesOptions,
    AutocompletePlace,
    Color,
    forLifeOf,
    LifecycleAware,
    Modal,
    Unmasks
} from '@lsq-frontend/common-ui';
import { getRouterLinkById, Icons } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { LsqLoadingModal, ModalService } from '@lsq-frontend/common-ui';
import { AdvancedSearchService } from '../../services/advanced-search.service';
import {
    AdvancedSearchForm,
    AdvancedSearchModalComponent
} from '../modals/advanced-search-modal.component';

@LifecycleAware()
@Component({
    selector: 'new-customer-credit',
    templateUrl: './new-customer-credit.component.html',
    styleUrls: ['./new-customer-credit.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class NewCustomerCreditComponent implements OnInit, OnDestroy {
    @ViewChild('noResultsBody') public noResultsBody: TemplateRef<any>;

    public formGroup = new FormGroup({
        name: new FormControl('', [LsqValidators.maxLength(100), Validators.required]),
        city: new FormControl('', [LsqValidators.maxLength(50), Validators.required]),
        state: new FormControl('', [LsqValidators.maxLength(30), Validators.required])
    });
    public icons = Icons;
    public color = Color;
    public states = AmericanStatesAndTerritoriesOptions;
    public creditRequestHistoryRouterLink = getRouterLinkById(
        atlas,
        REPORTS_CREDIT_REQUEST_HISTORY
    );

    public showResults = false;
    public selectedCustomers = [];
    public searchResults: SearchDebtor[];
    public approvedCustomers: AddCreditRequestResponse[];
    public declinedCustomers: AddCreditRequestResponse[];
    public pendingCustomers: AddCreditRequestResponse[];
    public isSearching = false;
    public advancedSearchForm: AdvancedSearchForm;
    public place: AutocompletePlace = {};
    public phoneUnmask = Unmasks.PhoneUnmask;
    public isAutoFilled = false;
    public isExistingCustomer = false;
    public addDebtorsIsSubmitting = false;

    constructor(
        private _appSettings: AppSettings,
        private _advancedSearchService: AdvancedSearchService,
        private _batchAddDebtorsForClientService: BatchAddDebtorsForClientService,
        private _batchAddCreditRequestsService: BatchAddCreditRequestsService,
        private _modalService: ModalService,
        private _searchDebtorsService: SearchDebtorsService,
        private _userService: UserService,
        private _activatedRoute: ActivatedRoute
    ) {}

    public ngOnInit(): void {
        this._batchAddCreditRequestsService.dataStream
            .pipe(forLifeOf(this))
            .subscribe((response) => {
                this.approvedCustomers = response.rows.filter(
                    (result) => result.creditRequestStatus === CreditRequestStatus.Approved
                );
                this.declinedCustomers = response.rows.filter(
                    (result) => result.creditRequestStatus === CreditRequestStatus.Denied
                );
                this.pendingCustomers = response.rows.filter(
                    (result) =>
                        // The Product team has indicated that HasExisting will be handled the same as Pending for the time being.
                        result.creditRequestStatus === CreditRequestStatus.Pending ||
                        result.creditRequestStatus === CreditRequestStatus.HasExisting
                );
                this._modalService.closeModal();
            });

        this._searchDebtorsService.dataStream.pipe(forLifeOf(this)).subscribe((response) => {
            this.searchResults = [...response.rows];
            this.showResults = true;
            this.isSearching = false;
            if (!response.rows.length && !this.advancedSearchForm) {
                this.showNoResultsModal();
            }
        });

        this._advancedSearchService.searchStream
            .pipe(forLifeOf(this))
            .subscribe((advancedSearchForm: AdvancedSearchForm) => {
                this.advancedSearchForm = { ...advancedSearchForm };
                this.formGroup.get('name').setValue(advancedSearchForm.name);
                this.formGroup.get('city').setValue(advancedSearchForm.primaryCity);
                this.formGroup.get('state').setValue(advancedSearchForm.primaryState);
                this.isSearching = true;
            });

        this._activatedRoute.queryParams.pipe(forLifeOf(this)).subscribe((queryParams) => {
            if (queryParams.name) {
                this.formGroup.controls.name.setValue(queryParams.name);
            }
        });
    }

    public ngOnDestroy(): void {}

    public search(): void {
        // Reset previous results.
        this.showResults = false;
        this.advancedSearchForm = null;

        let addressFilter = null;
        let postalCodeFilter = null;
        if (this.advancedSearchForm) {
            addressFilter = this.advancedSearchForm.secondaryAddress1
                ? this.advancedSearchForm.secondaryAddress1
                : this.advancedSearchForm.primaryAddress1;
            postalCodeFilter = this.advancedSearchForm.secondaryAddress1
                ? this.advancedSearchForm.secondaryPostalCode
                : this.advancedSearchForm.primaryPostalCode;
        }
        const request = new SearchDebtorsRequest({
            take: this._appSettings.defaultSearchLength,
            nameFilter: this.formGroup.value.name,
            stateFilter: this.formGroup.value.state,
            cityFilter: this.formGroup.value.city
        });
        this._searchDebtorsService.execute(request);
        this.isSearching = true;
    }

    public addResult(result: any): void {
        this.selectedCustomers.push({
            companyDetails: result,
            form: new FormGroup({
                amount: new FormControl('', [Validators.required, LsqValidators.currency])
            })
        });
        this.hideResults();
    }

    public addResultManually(): void {
        if (this.advancedSearchForm) {
            this.addResult(this.advancedSearchForm);
        }
    }

    public removeSelection(index: number): void {
        this.selectedCustomers.splice(index, 1);
    }

    public isDisabled(): boolean {
        return !(
            this.selectedCustomers.length &&
            this.selectedCustomers.every((customer) => customer.form.valid)
        );
    }

    public showNoResultsModal(): void {
        this._modalService.showModal(
            new Modal({
                id: 'no-search-results-modal',
                title: 'Your search did not return any results',
                width: '49rem',
                showCloseButton: true,
                templateRef: this.noResultsBody,
                showFooter: true,
                showOkButton: true,
                showCancelButton: true,
                cancelButtonText: 'Advanced Search',
                onCancelClick: () => this.showAdvancedSearchModal(this.advancedSearchForm),
                okButtonText: 'Search Again'
            })
        );
    }

    public hideResults(): void {
        this.formGroup.reset();
        this.showResults = false;
        this.advancedSearchForm = null;
    }

    public async requestCredit(): Promise<void> {
        const creditRequests = this.selectedCustomers.map(
            (customer) =>
                new AddDebtorForClientRequest({
                    ...customer.companyDetails,
                    primaryCountry: 'United States',
                    secondaryCountry: customer.companyDetails.isPoBox
                        ? 'United States'
                        : customer.companyDetails.secondaryCountry,
                    companyKey: this._userService.getCompanyKey(),
                    searchProvider: SearchProvider.Experian
                })
        );

        this.addDebtorsIsSubmitting = true;
        const response = await this._batchAddDebtorsForClientService.executeOnce(
            new BatchAddDebtorsForClientRequest({
                debtorsForClientRequest: creditRequests
            })
        );
        this.addDebtorsIsSubmitting = false;
        const request = new BatchAddCreditRequestsRequest();

        // Order of the response is guaranteed to match order of the request, but check just to be safe.
        if (response.rows.length === this.selectedCustomers.length) {
            // Check if any of the customers are existing.
            this.isExistingCustomer = response.rows.some((row) => row.isExisting);
            // Use the order of the debtorKeys in the response to correlate to the requestedAmount.
            request.creditRequests = this.selectedCustomers.map(
                (customer, customerIndex) =>
                    new AddCreditRequestRequest({
                        debtorKey: response.rows[customerIndex].debtorKey,
                        requestedAmount: customer.form.value.amount,
                        companyKey: this._userService.getCompanyKey()
                    })
            );
            this._batchAddCreditRequestsService.execute(request);
        }
        this._modalService.showModal(
            new LsqLoadingModal(
                'Please wait while we process your request',
                'This should only take a few moments.'
            )
        );
    }

    public showAdvancedSearchModal(form: AdvancedSearchForm): void {
        this._modalService.showModal(
            new Modal({
                id: 'advanced-search-modal',
                title: 'Enter details about your customer',
                component: AdvancedSearchModalComponent,
                showFooter: false,
                width: '71.5rem',
                data: form || {
                    isAutoFilled: this.isAutoFilled,
                    name: this.formGroup.value.name,
                    primaryCity: this.formGroup.value.city,
                    primaryState: this.formGroup.value.state,
                    primaryAddress1: this.place.address1,
                    phone: this.place.phoneNumber,
                    primaryPostalCode: this.place.postalCode
                }
            })
        );
        this.resetAutoFillFields();
    }

    public resetAutoFillFields(): void {
        this.place = {};
        this.isAutoFilled = false;
    }

    public areResultsPresent(): boolean {
        return (
            (this.approvedCustomers && !!this.approvedCustomers.length) ||
            (this.declinedCustomers && !!this.declinedCustomers.length) ||
            (this.pendingCustomers && !!this.pendingCustomers.length)
        );
    }

    public isResultAlreadySelected(result): boolean {
        return !!this.selectedCustomers.filter(
            (customer) => customer.companyDetails.dunsNumber === result.dunsNumber
        ).length;
    }

    public resetPage(): void {
        this.showResults = false;
        this.selectedCustomers = [];
        this.isSearching = false;
        delete this.searchResults;
        delete this.approvedCustomers;
        delete this.declinedCustomers;
        delete this.pendingCustomers;
        delete this.advancedSearchForm;
        this.isExistingCustomer = false;
    }

    public populateResults(place: AutocompletePlace, establishment: boolean): void {
        this.isAutoFilled = true;

        if (establishment) {
            this.formGroup.patchValue({
                name: place.companyName
            });
        }

        // If the Places API does not return phone number, replace it with blank.
        place.phoneNumber = place.phoneNumber
            ? place.phoneNumber.replace(this.phoneUnmask, '')
            : '';

        this.formGroup.patchValue({
            name: place.companyName,
            city: place.city
        });

        // Ensure that we don't populate this field with foreign states.
        if (Object.values(AmericanStateAbbreviations).includes(place.state)) {
            this.formGroup.patchValue({
                state: place.state
            });
        } else if (Object.keys(AmericanStateAbbreviations).includes(place.state)) {
            this.formGroup.patchValue({
                state: AmericanStateAbbreviations[place.state]
            });
        }

        this.place.address1 = place.address1;
        this.place.address2 = place.address2;
        this.place.phoneNumber = place.phoneNumber;
        this.place.postalCode = place.postalCode;
    }
}
